import {
  Box,
  Button,
  TextField,
  Typography,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Paper,
  CircularProgress,
  Alert,
  AppBar,
  Toolbar,
  IconButton,
  InputAdornment,
} from '@mui/material';
import React, { useState, useCallback } from 'react';
import StorefrontIcon from '@mui/icons-material/Storefront';
import SearchIcon from '@mui/icons-material/Search';
import NavigateBeforeIcon from '@mui/icons-material/NavigateBefore';
import NavigateNextIcon from '@mui/icons-material/NavigateNext';

const PER_PAGE = 100;
const SHOPIFY_LIMIT = 250;
const API_PATHS = ['/wp-json/wc/store/products', '/wp-json/wc/store/v1/products'];
const SHOPIFY_PATH = '/products.json';
const GOOGLEBOT_USER_AGENT =
  'Mozilla/5.0 (compatible; Googlebot/2.1; +http://www.google.com/bot.html)';

function decodeHtmlEntities(str) {
  if (str == null || typeof str !== 'string') return str ?? '';
  const el = document.createElement('textarea');
  el.innerHTML = str;
  return el.value;
}

function mapShopifyProductToRow(p, baseUrl) {
  const variant = p.variants?.[0];
  const price = variant?.price != null ? `$${variant.price}` : '—';
  const image =
    variant?.featured_image?.src ||
    p.images?.[0]?.src ||
    (p.image && (p.image.src || p.image)) ||
    '';
  const permalink = baseUrl && p.handle ? `${baseUrl.replace(/\/+$/, '')}/products/${p.handle}` : '#';
  return {
    id: p.id,
    name: decodeHtmlEntities(p.title || '—'),
    permalink,
    price,
    image,
    date: p.created_at || p.published_at || p.updated_at || '',
  };
}

function normalizeWebsite(input) {
  if (!input || typeof input !== 'string') return '';
  let url = input.trim();
  if (!url.startsWith('http://') && !url.startsWith('https://')) {
    url = 'https://' + url;
  }
  return url.replace(/\/+$/, '');
}

function parsePrice(prices) {
  if (!prices) return '—';
  const prefix = prices.currency_prefix || '';
  const price = prices.price;
  if (price == null || price === '') return '—';
  const minor = prices.currency_minor_unit != null ? parseInt(prices.currency_minor_unit, 10) : 2;
  const value = parseInt(price, 10);
  const formatted = minor === 0 ? String(value) : (value / Math.pow(10, minor)).toFixed(minor);
  return prefix + formatted;
}

function formatDateTime(isoString) {
  if (!isoString) return '—';
  try {
    const d = new Date(isoString);
    if (Number.isNaN(d.getTime())) return '—';
    return d.toLocaleString(undefined, {
      dateStyle: 'short',
      timeStyle: 'short',
    });
  } catch {
    return '—';
  }
}

function WooStoreProducts() {
  const [website, setWebsite] = useState('');
  const [products, setProducts] = useState([]);
  const [page, setPage] = useState(1);
  const [totalPages, setTotalPages] = useState(0);
  const [totalItems, setTotalItems] = useState(0);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');
  const [pageInput, setPageInput] = useState('1');

  const fetchProducts = useCallback(
    async (pageNum = 1) => {
      const baseUrl = normalizeWebsite(website);
      if (!baseUrl) {
        setError('Please enter a website (e.g. abc.com)');
        return;
      }

      setLoading(true);
      setError('');
      let res = null;
      let lastErr = null;

      for (const apiPath of API_PATHS) {
        const url = `${baseUrl}${apiPath}?page=${pageNum}&orderby=date&order=desc&per_page=${PER_PAGE}`;
        const proxyUrl = `/api/proxy?url=${encodeURIComponent(url)}`;
        try {
          res = await fetch(proxyUrl);
          if (res.ok) break;
          lastErr = new Error(`HTTP ${res.status}`);
        } catch (e) {
          lastErr = e;
        }
      }

      if (res && res.ok) {
        try {
          const totalPagesHeader = res.headers.get('X-WP-TotalPages');
          const totalHeader = res.headers.get('X-WP-Total');
          const total = totalHeader ? parseInt(totalHeader, 10) : 0;
          const pages = totalPagesHeader ? parseInt(totalPagesHeader, 10) : 1;
          const data = await res.json();
          if (!Array.isArray(data)) {
            throw new Error('Invalid response: expected array of products');
          }
          const fetchTime = new Date().toISOString();
          const rows = data.map((p) => ({
            id: p.id,
            name: decodeHtmlEntities(p.name || '—'),
            permalink: p.permalink || '#',
            price: parsePrice(p.prices),
            image: p.images?.[0]?.src || p.images?.[0]?.thumbnail || '',
            date: p.date_created || p.date_modified || p.date || fetchTime,
          }));
          setProducts(rows);
          setTotalPages(pages);
          setTotalItems(total);
          setPage(pageNum);
          setPageInput(String(pageNum));
        } catch (e) {
          setError(e.message || 'Fetch failed');
          setProducts([]);
          setTotalPages(0);
          setTotalItems(0);
        }
        setLoading(false);
        return;
      }

      const shopifyUrl = `${baseUrl}${SHOPIFY_PATH}?limit=${SHOPIFY_LIMIT}&page=${pageNum}&sort=created_at`;
      const proxyUrl = `/api/proxy?url=${encodeURIComponent(shopifyUrl)}`;
      try {
        res = await fetch(proxyUrl);
        if (!res.ok) {
          setError(lastErr?.message || `Fetch failed (HTTP ${res.status})`);
          setProducts([]);
          setTotalPages(0);
          setTotalItems(0);
          setLoading(false);
          return;
        }
        const data = await res.json();
        const list = Array.isArray(data?.products) ? data.products : [];
        const rows = list.map((p) => mapShopifyProductToRow(p, baseUrl));
        const hasMore = list.length >= SHOPIFY_LIMIT;
        setProducts(rows);
        setTotalPages(hasMore ? pageNum + 1 : pageNum);
        setTotalItems((pageNum - 1) * SHOPIFY_LIMIT + rows.length);
        setPage(pageNum);
        setPageInput(String(pageNum));
      } catch (e) {
        setError(e.message || lastErr?.message || 'Fetch failed');
        setProducts([]);
        setTotalPages(0);
        setTotalItems(0);
      } finally {
        setLoading(false);
      }
    },
    [website]
  );

  const loadDemo = useCallback(async () => {
    setLoading(true);
    setError('');
    try {
      const res = await fetch('/json2.json', {
        headers: { 'User-Agent': GOOGLEBOT_USER_AGENT },
      });
      if (!res.ok) throw new Error('Failed to load demo data');
      const data = await res.json();
      const list = Array.isArray(data?.products) ? data.products : [];
      const rows = list.map((p) => mapShopifyProductToRow(p, ''));
      setProducts(rows);
      setTotalPages(1);
      setTotalItems(rows.length);
      setPage(1);
      setPageInput('1');
    } catch (e) {
      setError(e.message || 'Failed to load demo data');
      setProducts([]);
    } finally {
      setLoading(false);
    }
  }, []);

  const handleFetch = () => fetchProducts(1);

  const goPrev = () => {
    if (page <= 1) return;
    const next = page - 1;
    setPage(next);
    setPageInput(String(next));
    fetchProducts(next);
  };

  const goNext = () => {
    if (page >= totalPages) return;
    const next = page + 1;
    setPage(next);
    setPageInput(String(next));
    fetchProducts(next);
  };

  const handlePageInputChange = (e) => {
    setPageInput(e.target.value);
  };

  const goToPage = () => {
    const num = parseInt(pageInput, 10);
    if (Number.isNaN(num) || num < 1 || num > totalPages) return;
    setPage(num);
    fetchProducts(num);
  };

  const hasData = products.length > 0;
  const showPagination = hasData && totalPages > 0;
  const mid = Math.ceil(products.length / 2);
  const productsPart1 = products.slice(0, mid);
  const productsPart2 = products.slice(mid);

  const tableHeaderCells = (
    <TableRow>
      <TableCell sx={{ fontWeight: 600, bgcolor: '#f9fafb', color: '#6b7280', borderColor: '#e5e7eb', py: 2 }}>Image</TableCell>
      <TableCell sx={{ fontWeight: 600, bgcolor: '#f9fafb', color: '#6b7280', borderColor: '#e5e7eb', py: 2 }}>Price</TableCell>
      <TableCell sx={{ fontWeight: 600, bgcolor: '#f9fafb', color: '#6b7280', borderColor: '#e5e7eb', py: 2 }}>Name</TableCell>
      <TableCell sx={{ fontWeight: 600, bgcolor: '#f9fafb', color: '#6b7280', borderColor: '#e5e7eb', py: 2 }}>Link</TableCell>
      <TableCell sx={{ fontWeight: 600, bgcolor: '#f9fafb', color: '#6b7280', borderColor: '#e5e7eb', py: 2, whiteSpace: 'nowrap' }}>Time</TableCell>
    </TableRow>
  );

  const renderProductRow = (row, idx) => (
    <TableRow
      key={row.id}
      sx={{
        '&:hover': { bgcolor: '#f9fafb' },
        bgcolor: idx % 2 === 0 ? '#fff' : '#fafafa',
      }}
    >
      <TableCell sx={{ borderColor: '#e5e7eb', py: 1.5 }}>
        {row.image ? (
          <img
            src={row.image}
            alt=""
            style={{ width: 56, height: 56, objectFit: 'cover', borderRadius: 8, border: '1px solid #e5e7eb' }}
            loading="lazy"
          />
        ) : (
          <Box sx={{ width: 56, height: 56, borderRadius: 8, bgcolor: '#f3f4f6', display: 'flex', alignItems: 'center', justifyContent: 'center', color: '#9ca3af', fontSize: '0.75rem' }}>—</Box>
        )}
      </TableCell>
      <TableCell sx={{ borderColor: '#e5e7eb', color: '#6366f1', fontWeight: 600 }}>{row.price}</TableCell>
      <TableCell sx={{ borderColor: '#e5e7eb', color: '#1a1a1a', maxWidth: 360 }}>{row.name}</TableCell>
      <TableCell sx={{ borderColor: '#e5e7eb' }}>
        {row.permalink && row.permalink !== '#' ? (
          <Button component="a" href={row.permalink} target="_blank" rel="noopener noreferrer" size="small" sx={{ color: '#6366f1', textTransform: 'none', '&:hover': { bgcolor: '#eef2ff', color: '#4f46e5' } }}>View product</Button>
        ) : (
          <Typography component="span" sx={{ color: '#9ca3af' }}>—</Typography>
        )}
      </TableCell>
      <TableCell sx={{ borderColor: '#e5e7eb', color: '#6b7280', whiteSpace: 'nowrap', fontSize: '0.8125rem' }}>{formatDateTime(row.date)}</TableCell>
    </TableRow>
  );

  return (
    <Box
      sx={{
        height: '100vh',
        overflow: 'hidden',
        display: 'flex',
        flexDirection: 'column',
        bgcolor: '#f5f5f7',
        color: '#1a1a1a',
      }}
    >
      <AppBar
        position="static"
        elevation={0}
        sx={{
          bgcolor: '#fff',
          borderBottom: '1px solid #e5e7eb',
          color: '#1a1a1a',
        }}
      >
        <Toolbar sx={{ gap: 1, py: 0.5 }}>
          <StorefrontIcon sx={{ color: '#6366f1', fontSize: 28 }} />
          <Typography variant="h6" sx={{ fontWeight: 600, letterSpacing: '-0.02em', color: '#1a1a1a' }}>
            Store Products
          </Typography>
        </Toolbar>
      </AppBar>

      <Box sx={{ flex: 1, display: 'flex', flexDirection: 'column', p: 3, minHeight: 0, overflow: 'hidden' }}>
        <Paper
          elevation={0}
          sx={{
            p: 2.5,
            mb: 2,
            borderRadius: 2,
            bgcolor: '#fff',
            border: '1px solid #e5e7eb',
          }}
        >
          <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 2, alignItems: 'center' }}>
            <TextField
              label="Website"
              placeholder="abc.com or https://example.com"
              value={website}
              onChange={(e) => setWebsite(e.target.value)}
              onKeyDown={(e) => e.key === 'Enter' && handleFetch()}
              size="small"
              sx={{
                minWidth: 320,
                '& .MuiOutlinedInput-root': {
                  bgcolor: '#fafafa',
                  borderRadius: 1.5,
                  '& fieldset': { borderColor: '#e5e7eb' },
                  '&:hover fieldset': { borderColor: '#d1d5db' },
                  '&.Mui-focused fieldset': { borderColor: '#6366f1', borderWidth: 1 },
                },
                '& .MuiInputLabel-root': { color: '#6b7280' },
                '& .MuiInputBase-input': { color: '#1a1a1a' },
              }}
              InputProps={{
                startAdornment: (
                  <InputAdornment position="start">
                    <SearchIcon sx={{ color: '#9ca3af', fontSize: 20 }} />
                  </InputAdornment>
                ),
              }}
            />
            <Button
              variant="contained"
              onClick={handleFetch}
              disabled={loading}
              startIcon={loading ? <CircularProgress size={18} color="inherit" /> : null}
              sx={{
                bgcolor: '#6366f1',
                color: '#fff',
                fontWeight: 600,
                px: 3,
                py: 1.25,
                borderRadius: 1.5,
                textTransform: 'none',
                '&:hover': { bgcolor: '#4f46e5' },
              }}
            >
              {loading ? 'Loading…' : 'Fetch products'}
            </Button>

          </Box>
        </Paper>

        {error && (
          <Alert
            severity="error"
            onClose={() => setError('')}
            sx={{
              mb: 2,
              borderRadius: 1.5,
              bgcolor: '#fef2f2',
              border: '1px solid #fecaca',
              '& .MuiAlert-message': { color: '#b91c1c' },
            }}
          >
            {error}
          </Alert>
        )}

        {showPagination && (
          <Paper
            elevation={0}
            sx={{
              p: 1.5,
              mb: 2,
              borderRadius: 1.5,
              bgcolor: '#fff',
              border: '1px solid #e5e7eb',
              display: 'flex',
              alignItems: 'center',
              gap: 2,
              flexWrap: 'wrap',
            }}
          >
            <IconButton
              onClick={goPrev}
              disabled={page <= 1}
              size="small"
              sx={{
                color: page <= 1 ? '#9ca3af' : '#4b5563',
                '&:hover': { bgcolor: '#f3f4f6', color: '#1a1a1a' },
                '&.Mui-disabled': { color: '#d1d5db' },
              }}
            >
              <NavigateBeforeIcon />
            </IconButton>
            <IconButton
              onClick={goNext}
              disabled={page >= totalPages}
              size="small"
              sx={{
                color: page >= totalPages ? '#9ca3af' : '#4b5563',
                '&:hover': { bgcolor: '#f3f4f6', color: '#1a1a1a' },
                '&.Mui-disabled': { color: '#d1d5db' },
              }}
            >
              <NavigateNextIcon />
            </IconButton>
            <Typography variant="body2" sx={{ color: '#6b7280', fontWeight: 500 }}>
              Page {page} of {totalPages}
            </Typography>
            <Typography variant="body2" sx={{ color: '#9ca3af' }}>
              {totalItems} items
            </Typography>
            <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, ml: 1 }}>
              <TextField
                size="small"
                type="number"
                placeholder="Page"
                value={pageInput}
                onChange={handlePageInputChange}
                inputProps={{ min: 1, max: totalPages }}
                sx={{
                  width: 72,
                  '& .MuiOutlinedInput-root': {
                    bgcolor: '#fafafa',
                    borderRadius: 1,
                    fontSize: '0.875rem',
                    '& fieldset': { borderColor: '#e5e7eb' },
                  },
                  '& .MuiInputBase-input': { color: '#1a1a1a', py: 0.75 },
                }}
              />
              <Button
                size="small"
                onClick={goToPage}
                sx={{
                  color: '#6366f1',
                  textTransform: 'none',
                  '&:hover': { bgcolor: '#eef2ff' },
                }}
              >
                Go
              </Button>
            </Box>
          </Paper>
        )}

        <Paper
          elevation={0}
          component={Box}
          sx={{
            flex: 1,
            minHeight: 0,
            display: 'flex',
            flexDirection: 'column',
            borderRadius: 2,
            bgcolor: '#fff',
            border: '1px solid #e5e7eb',
            overflow: 'hidden',
          }}
        >
          {hasData ? (
            <Box sx={{ flex: 1, minHeight: 0, display: 'flex', flexDirection: { xs: 'column', md: 'row' }, gap: 2, overflow: 'hidden', p: 2 }}>
              <Paper elevation={0} sx={{ flex: 1, minHeight: 0, display: 'flex', flexDirection: 'column', borderRadius: 1.5, border: '1px solid #e5e7eb', overflow: 'hidden' }}>
                <Typography variant="subtitle2" sx={{ px: 2, py: 1, bgcolor: '#f9fafb', borderBottom: '1px solid #e5e7eb', color: '#6b7280', fontWeight: 600 }}>
                  Table 1 (1–{productsPart1.length})
                </Typography>
                <TableContainer sx={{ flex: 1, minHeight: 0, overflow: 'auto' }}>
                  <Table stickyHeader size="medium">
                    <TableHead>{tableHeaderCells}</TableHead>
                    <TableBody>{productsPart1.map((row, idx) => renderProductRow(row, idx))}</TableBody>
                  </Table>
                </TableContainer>
              </Paper>
              <Paper elevation={0} sx={{ flex: 1, minHeight: 0, display: 'flex', flexDirection: 'column', borderRadius: 1.5, border: '1px solid #e5e7eb', overflow: 'hidden' }}>
                <Typography variant="subtitle2" sx={{ px: 2, py: 1, bgcolor: '#f9fafb', borderBottom: '1px solid #e5e7eb', color: '#6b7280', fontWeight: 600 }}>
                  Table 2 {productsPart2.length > 0 ? `(${productsPart1.length + 1}–${products.length})` : '(empty)'}
                </Typography>
                <TableContainer sx={{ flex: 1, minHeight: 0, overflow: 'auto' }}>
                  <Table stickyHeader size="medium">
                    <TableHead>{tableHeaderCells}</TableHead>
                    <TableBody>{productsPart2.map((row, idx) => renderProductRow(row, idx))}</TableBody>
                  </Table>
                </TableContainer>
              </Paper>
            </Box>
          ) : null}

          {!loading && !error && products.length === 0 && (
            <Box
              sx={{
                flex: 1,
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'center',
                flexDirection: 'column',
                gap: 1,
                color: '#6b7280',
              }}
            >
              <StorefrontIcon sx={{ fontSize: 48, opacity: 0.35, color: '#9ca3af' }} />
              <Typography variant="body1">
                {website ? 'No products found. Try another website or load demo.' : 'Enter a website and fetch products, or load demo (json2.json).'}
              </Typography>
            </Box>
          )}
        </Paper>
      </Box>
    </Box>
  );
}

export default WooStoreProducts;
